import React, { useMemo } from 'react';
import { Tree } from 'antd';
import { BrandSelectorContentProps } from '../../types';
import { foldSearch } from '../../../../utils/searchFold';

const BrandSelectorContent: React.FC<BrandSelectorContentProps> = ({
  brandDataLoading,
  brandOptions,
  state,
  fieldKey,
  updateTreeSelectState,
  mergeCompleteSubbrandsToParent
}) => {
  // 🎯 Memoize tree data to avoid recalculating filtered results
  // Dependencies: brandOptions (cached per countryCode, same reference reused for same market) and searchValue (only changes when user types)
  // This ensures that:
  // 1. When clicking different core brands in the SAME market, brandOptions reference stays the same (from cache)
  //    → useMemo sees same brandOptions reference → reuses cached filtered result → NO recalculation
  // 2. When searchValue changes but brandOptions hasn't, the filtered result is reused from cache
  // 3. When switching to a different market, brandOptions gets new reference → useMemo recalculates
  const treeData = useMemo(() => {
    if (brandDataLoading || !brandOptions || brandOptions.length === 0) {
      return null;
    }
    
    // If no search value, return all brandOptions directly (no filtering needed)
    if (!state.searchValue || state.searchValue.trim() === '') {
      return brandOptions;
    }
    
    // Filter tree data based on searchValue
    const query = foldSearch(state.searchValue);
    const filterNode = (node: any): any | null => {
      const titleMatch = foldSearch(node.title).includes(query);
      const hasMatchingChildren = node.children?.some((child: any) =>
        foldSearch(child.title).includes(query)
      );

      if (titleMatch || hasMatchingChildren) {
        return {
          ...node,
          children:
            node.children
              ?.map((child: any) => filterNode(child))
              .filter(Boolean) || [],
        };
      }

      return null;
    };

    const filtered = brandOptions
      .map((node) => filterNode(node))
      .filter(Boolean);
    
    console.log(`[TREE_RENDER] 🌲 Tree component receiving data:`, {
      searchValue: state.searchValue,
      treeDataCount: filtered?.length || 0,
      checkedKeys: state.tempSelectedBrands,
      checkedKeysCount: state.tempSelectedBrands?.length || 0
    });
    console.log(`[TREE_RENDER] 📋 Tree data structure:`, filtered?.map((item: any) => ({
      title: item.title,
      value: item.value,
      hasChildren: !!item.children,
      childrenCount: item.children?.length || 0,
      children: item.children?.map((child: any) => ({
        title: child.title,
        value: child.value
      })) || []
    })));
    
    return filtered;
  }, [state.searchValue, brandDataLoading, brandOptions]);

  return (
    <div className="p-4">
      <div className="max-h-[200px] overflow-y-auto">
        {brandDataLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-sm text-gray-500">Loading brands...</span>
          </div>
        ) : (
          <Tree
            checkable
            checkedKeys={state.tempSelectedBrands}
            onCheck={(checkedKeys) => {
              console.log("[TREE_RENDER] 🎯 Tree onCheck triggered:");
              console.log("[TREE_RENDER] Previous checkedKeys:", state.tempSelectedBrands);
              console.log("[TREE_RENDER] New checkedKeys:", checkedKeys);
              // antd hands back either an array or a { checked } object
              const keys = Array.isArray(checkedKeys) ? checkedKeys : checkedKeys.checked;
              // Take the selection as-is; a parent brand is never auto-expanded into its subbrands
              const mergedSelection = mergeCompleteSubbrandsToParent(keys as string[]);
              console.log("[TREE_RENDER] User selection with smart merge:", {
                original: keys,
                merged: mergedSelection
              });
              updateTreeSelectState(fieldKey, {
                tempSelectedBrands: mergedSelection
              });
            }}
            treeData={treeData || []}
            height={180}
            showLine={false}
            showIcon={false}
            blockNode
          />
        )}
      </div>
    </div>
  );
};

export default BrandSelectorContent;

